import type {
  AlertRuleDto,
  AlertRuleListQuery,
  AlertTestResultDto,
  AuditEntryDto,
  AuditQuery,
  BotDetailDto,
  BotDto,
  BotEventDto,
  BotEventQuery,
  BotListQuery,
  CommandAcceptedDto,
  CreateBotRequest,
  CreateMcAccountRequest,
  CreateAlertRuleRequest,
  CreateServerRequest,
  DashboardSummaryDto,
  LoginRequest,
  LoginResponse,
  McAccountDto,
  McAuthStatusDto,
  MeResponse,
  OrgCurrentDto,
  Paginated,
  ProbeRequest,
  RefreshResponse,
  RegisterRequest,
  ServerDto,
  ServerProbeDto,
  SystemInfoDto,
  TotpEnableResponse,
  TotpSetupDto,
  UpdateAlertRuleRequest,
  UpdateBotRequest,
  UpdateMcAccountRequest,
  UpdateProfileRequest,
  UserDto,
  ViewerSessionDto,
  ViewerSessionListDto
} from './dto'
import { NetworkError, toApiError } from './api-error'

/**
 * Типизированный fetch-клиент (docs/06 §9).
 *
 * Что здесь важного:
 *  - access-токен живёт **только в памяти** (US-1 AC1): ни localStorage, ни cookie;
 *  - refresh делается по 401 автоматически, с де-дупликацией (single-flight)
 *    и повтором исходного запроса — включая **тот же** `Idempotency-Key`;
 *  - `X-Org-Id` уходит со всеми запросами, кроме `/auth/*`;
 *  - ответ `application/problem+json` превращается в `ApiError` с полем `code`.
 */

const API_PREFIX = '/api'

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export interface RequestOptions {
  method?: HttpMethod
  body?: unknown
  params?: Record<string, string | number | boolean | undefined | null>
  /** docs/06 §1: `Idempotency-Key` для POST, меняющих состояние бота. */
  idempotent?: boolean
  /** Не пытаться обновлять токен (сами `/auth/*` эндпоинты). */
  skipAuthRefresh?: boolean
  signal?: AbortSignal
  headers?: Record<string, string>
}

// ─────────────────────── состояние сессии в памяти ───────────────────────

let accessToken: string | null = null
let activeOrgId: string | null = null
let sessionExpiredHandler: (() => void) | null = null

export const tokenStore = {
  get(): string | null {
    return accessToken
  },
  set(token: string | null): void {
    accessToken = token
  },
  clear(): void {
    accessToken = null
  }
}

export const orgStore = {
  get(): string | null {
    return activeOrgId
  },
  set(orgId: string | null): void {
    activeOrgId = orgId
  }
}

/** Регистрирует реакцию на «refresh не помог» — приложение уводит на /login. */
export function onSessionExpired(handler: (() => void) | null): void {
  sessionExpiredHandler = handler
}

/** Полный сброс состояния клиента. Используется тестами и при logout. */
export function resetApiClient(): void {
  accessToken = null
  activeOrgId = null
  refreshInFlight = null
  csrfToken = null
}

// ─────────────────────────── refresh single-flight ───────────────────────────

let refreshInFlight: Promise<string> | null = null

/** Сколько раз клиент реально ходил за refresh — крючок для тестов. */
let refreshCallCount = 0
export function getRefreshCallCount(): number {
  return refreshCallCount
}
export function resetRefreshCallCount(): void {
  refreshCallCount = 0
}

/**
 * Обновление access-токена. Параллельные 401 не порождают N запросов:
 * все ждут один и тот же промис (single-flight).
 */
export function refreshAccessToken(): Promise<string> {
  if (refreshInFlight !== null) return refreshInFlight

  refreshCallCount += 1
  refreshInFlight = doRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

async function doRefresh(): Promise<string> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  // docs/06 §2: refresh идёт по cookie + double-submit CSRF-токен.
  const csrf = readCsrfCookie()
  if (csrf !== null) headers['X-CSRF-Token'] = csrf

  const response = await safeFetch(`${API_PREFIX}/auth/refresh`, {
    method: 'POST',
    headers,
    credentials: 'include'
  })

  if (!response.ok) {
    accessToken = null
    sessionExpiredHandler?.()
    throw toApiError(response.status, await readBody(response), `${API_PREFIX}/auth/refresh`)
  }

  const data = (await response.json()) as RefreshResponse
  accessToken = data.accessToken
  // Сервер ротирует и refresh, и csrf — запоминаем новый, иначе следующий
  // refresh уйдёт с устаревшим заголовком.
  if (typeof data.csrf === 'string') csrfToken = data.csrf
  return data.accessToken
}

/**
 * CSRF-токен (double-submit). Основной источник — ТЕЛО ответа `/auth/login` и
 * `/auth/refresh`: значение живёт в памяти модуля.
 *
 * Почему не только cookie, как было раньше. Достать токен из `document.cookie`
 * можно лишь когда у cookie подходящий `Path`, и любая ошибка в этом месте
 * превращалась в «каждый F5 выкидывает на логин»: сначала cookie выдавалась с
 * `Path=/api/auth` и была невидима для страниц SPA, потом — когда путь
 * исправили — у пользователей осталась вторая cookie с тем же именем и старым
 * путём, браузер отправлял обе, а сервер разбирал первую (старую).
 *
 * Значение из тела от этого не зависит вообще. Cookie остаётся тем, с чем
 * сервер сравнивает заголовок.
 */
let csrfToken: string | null = null

export function setCsrfToken(value: string | null): void {
  csrfToken = value
}

export function readCsrfCookie(): string | null {
  if (csrfToken !== null) return csrfToken
  // Фоллбэк на cookie: нужен ровно один раз — на самый первый refresh после
  // перезагрузки страницы, когда тела ответа ещё не было.
  const raw = globalThis.document?.cookie ?? ''
  for (const part of raw.split(';')) {
    const [name, ...rest] = part.trim().split('=')
    if (name === 'apiary_xsrf') return decodeURIComponent(rest.join('='))
  }
  return null
}

// ──────────────────────────────── ядро запроса ────────────────────────────────

function buildUrl(path: string, params?: RequestOptions['params']): string {
  const url = `${API_PREFIX}${path}`
  if (params === undefined) return url
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }
  const query = search.toString()
  return query === '' ? url : `${url}?${query}`
}

function buildHeaders(options: RequestOptions, isAuthPath: boolean): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (accessToken !== null) headers.Authorization = `Bearer ${accessToken}`
  if (activeOrgId !== null && !isAuthPath) headers['X-Org-Id'] = activeOrgId
  // CSRF-заголовок нужен не только refresh'у: `/auth/logout` проходит ту же
  // проверку double-submit. Раньше он ставился лишь в `doRefresh`, и логаут
  // выживал только потому, что cookie вообще не читалась (её выдавали с
  // Path=/api/auth) и серверная проверка уходила в early-return. Как только
  // путь cookie исправили, логаут без этого заголовка начал бы отдавать 401.
  if (headers['X-CSRF-Token'] === undefined) {
    const csrf = readCsrfCookie()
    if (csrf !== null) headers['X-CSRF-Token'] = csrf
  }
  // Ключ генерируется один раз на логический запрос: повтор после refresh
  // обязан прийти с тем же ключом, иначе идемпотентность бессмысленна.
  if (options.idempotent === true && headers['Idempotency-Key'] === undefined) {
    headers['Idempotency-Key'] = crypto.randomUUID()
  }
  return headers
}

async function safeFetch(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new NetworkError('fetch failed', cause)
  }
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text()
  if (text === '') return undefined
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

/**
 * Один запрос к API. `T = void` для 204.
 *
 * Поток при 401: refresh (single-flight) → повтор ровно один раз с теми же
 * заголовками. Второй 401 означает «сессия умерла» — зовём `onSessionExpired`.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const isAuthPath = path.startsWith('/auth/')
  const url = buildUrl(path, options.params)
  const headers = buildHeaders(options, isAuthPath)
  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal === undefined ? {} : { signal: options.signal })
  }

  let response = await safeFetch(url, init)

  const canRefresh = options.skipAuthRefresh !== true && !isAuthPath
  if (response.status === 401 && canRefresh) {
    // Ошибка refresh пробрасывается как есть: `doRefresh` уже позвал
    // `onSessionExpired` и обнулил токен.
    const token = await refreshAccessToken()
    // Тот же init, только с новым токеном — Idempotency-Key сохраняется.
    const replayHeaders = { ...headers, Authorization: `Bearer ${token}` }
    response = await safeFetch(url, { ...init, headers: replayHeaders })
    if (response.status === 401) {
      accessToken = null
      sessionExpiredHandler?.()
    }
  }

  if (!response.ok) {
    throw toApiError(response.status, await readBody(response), url)
  }

  if (response.status === 204) return undefined as T
  const body = await readBody(response)
  return body as T
}

/** Как `request`, но возвращает поток (CSV-экспорт логов, docs/06 §5). */
export async function requestBlob(path: string, params?: RequestOptions['params']): Promise<Blob> {
  const url = buildUrl(path, params)
  const headers = buildHeaders({}, false)
  let response = await safeFetch(url, { headers, credentials: 'include' })
  if (response.status === 401) {
    const token = await refreshAccessToken()
    response = await safeFetch(url, {
      headers: { ...headers, Authorization: `Bearer ${token}` },
      credentials: 'include'
    })
  }
  if (!response.ok) throw toApiError(response.status, await readBody(response), url)
  return response.blob()
}

// ─────────────────────────────── эндпоинты ───────────────────────────────

export const api = {
  auth: {
    async login(dto: LoginRequest): Promise<LoginResponse> {
      const data = await request<LoginResponse>('/auth/login', { method: 'POST', body: dto })
      accessToken = data.accessToken
      csrfToken = data.csrf ?? null
      activeOrgId = data.orgs[0]?.orgId ?? null
      return data
    },
    register: (dto: RegisterRequest): Promise<{ user: UserDto }> =>
      request<{ user: UserDto }>('/auth/register', { method: 'POST', body: dto }),
    me: (): Promise<MeResponse> => request<MeResponse>('/auth/me'),
    /**
     * Правка профиля. Ответ — та же форма, что у `GET /auth/me`, поэтому вызов
     * заменяет содержимое сессии целиком и второй запрос не нужен.
     */
    updateMe: (dto: UpdateProfileRequest): Promise<MeResponse> =>
      request<MeResponse>('/auth/me', { method: 'PATCH', body: dto }),
    refresh: refreshAccessToken,
    async logout(): Promise<void> {
      await request<void>('/auth/logout', { method: 'POST', skipAuthRefresh: true })
      resetApiClient()
    },
    async logoutAll(): Promise<void> {
      await request<void>('/auth/logout-all', { method: 'POST', skipAuthRefresh: true })
      resetApiClient()
    },
    /**
     * Смена пароля. Сервер после неё гасит все сессии и бампает tokenVersion,
     * поэтому клиент обязан сбросить своё состояние — иначе следующий запрос
     * уйдёт с уже недействительным токеном и вернётся 401 «на ровном месте».
     */
    async changePassword(currentPassword: string, newPassword: string): Promise<void> {
      await request<void>('/auth/password', {
        method: 'POST',
        body: { currentPassword, newPassword }
      })
      resetApiClient()
    },

    /**
     * 2FA (docs/06 §2). Ни один из трёх вызовов не сбрасывает клиента:
     *  - `totpSetup` выдаёт НОВЫЙ черновик секрета при каждом вызове;
     *  - `totpEnable` гасит чужие сессии, но текущую оставляет живой — иначе
     *    экран с одноразовыми recovery-кодами закрылся бы редиректом на логин
     *    раньше, чем их успели переписать;
     *  - `totpDisable` принимает и TOTP, и recovery-код (телефон мог потеряться).
     */
    totpSetup: (): Promise<TotpSetupDto> =>
      request<TotpSetupDto>('/auth/totp/setup', { method: 'POST' }),
    totpEnable: (code: string): Promise<TotpEnableResponse> =>
      request<TotpEnableResponse>('/auth/totp/enable', { method: 'POST', body: { code } }),
    totpDisable: (password: string, code: string): Promise<void> =>
      request<void>('/auth/totp/disable', { method: 'POST', body: { password, code } })
  },

  dashboard: {
    summary: (): Promise<DashboardSummaryDto> => request<DashboardSummaryDto>('/dashboard/summary')
  },

  bots: {
    list: (query: BotListQuery = {}): Promise<Paginated<BotDto>> =>
      request<Paginated<BotDto>>('/bots', { params: { ...query } }),
    get: (id: string): Promise<BotDetailDto> => request<BotDetailDto>(`/bots/${id}`),
    create: (dto: CreateBotRequest): Promise<BotDto> =>
      request<BotDto>('/bots', { method: 'POST', body: dto, idempotent: true }),
    update: (id: string, dto: UpdateBotRequest): Promise<BotDto> =>
      request<BotDto>(`/bots/${id}`, { method: 'PATCH', body: dto }),
    remove: (id: string): Promise<void> => request<void>(`/bots/${id}`, { method: 'DELETE' }),
    start: (id: string): Promise<CommandAcceptedDto> =>
      request<CommandAcceptedDto>(`/bots/${id}/start`, { method: 'POST', idempotent: true }),
    stop: (id: string, reason?: string): Promise<CommandAcceptedDto> =>
      request<CommandAcceptedDto>(`/bots/${id}/stop`, {
        method: 'POST',
        body: reason === undefined ? {} : { reason },
        idempotent: true
      }),
    restart: (id: string): Promise<CommandAcceptedDto> =>
      request<CommandAcceptedDto>(`/bots/${id}/restart`, { method: 'POST', idempotent: true }),
    chat: (id: string, message: string): Promise<CommandAcceptedDto> =>
      request<CommandAcceptedDto>(`/bots/${id}/chat`, {
        method: 'POST',
        body: { message },
        idempotent: true
      }),
    events: (id: string, query: BotEventQuery = {}): Promise<Paginated<BotEventDto>> =>
      request<Paginated<BotEventDto>>(`/bots/${id}/events`, { params: { ...query } }),
    exportUrl: (id: string): string => `${API_PREFIX}/bots/${id}/events/export`,
    /**
     * CSV-экспорт нельзя открыть обычной ссылкой: токен живёт в памяти, а
     * `<a download>` не умеет ставить `Authorization`. Поэтому качаем blob'ом.
     */
    exportCsv: (id: string, query: BotEventQuery = {}): Promise<Blob> =>
      // Фильтры уходят на сервер: выгружать 100k строк, чтобы пользователь
      // потом искал в них глазами то, что уже отфильтровал на экране, — глупо.
      requestBlob(`/bots/${id}/events/export`, { ...query })
  },

  mcAccounts: {
    list: (): Promise<Paginated<McAccountDto>> => request<Paginated<McAccountDto>>('/mc-accounts'),
    create: (dto: CreateMcAccountRequest): Promise<McAccountDto> =>
      request<McAccountDto>('/mc-accounts', { method: 'POST', body: dto, idempotent: true }),
    reauth: (id: string): Promise<{ pollVia: string }> =>
      request<{ pollVia: string }>(`/mc-accounts/${id}/auth`, { method: 'POST', idempotent: true }),
    authStatus: (id: string): Promise<McAuthStatusDto> =>
      request<McAuthStatusDto>(`/mc-accounts/${id}/auth-status`),
    update: (id: string, dto: UpdateMcAccountRequest): Promise<McAccountDto> =>
      request<McAccountDto>(`/mc-accounts/${id}`, { method: 'PATCH', body: dto }),
    remove: (id: string): Promise<void> => request<void>(`/mc-accounts/${id}`, { method: 'DELETE' })
  },

  servers: {
    list: (): Promise<Paginated<ServerDto>> => request<Paginated<ServerDto>>('/servers'),
    create: (dto: CreateServerRequest): Promise<ServerDto> =>
      request<ServerDto>('/servers', { method: 'POST', body: dto }),
    update: (id: string, dto: Partial<CreateServerRequest>): Promise<ServerDto> =>
      request<ServerDto>(`/servers/${id}`, { method: 'PATCH', body: dto }),
    remove: (id: string): Promise<void> => request<void>(`/servers/${id}`, { method: 'DELETE' }),
    /**
     * Форс-пинг. `signal` не роскошь: у fetch нет своего таймаута, и без него
     * зависший запрос оставляет кнопку в вечном «крутится» — экран серверов
     * ставит собственный дедлайн (см. `ServersScreen`).
     */
    ping: (id: string, signal?: AbortSignal): Promise<ServerProbeDto> =>
      request<ServerProbeDto>(`/servers/${id}/ping`, {
        method: 'POST',
        ...(signal === undefined ? {} : { signal })
      }),
    probe: (dto: ProbeRequest): Promise<ServerProbeDto> =>
      request<ServerProbeDto>('/servers/probe', { method: 'POST', body: dto })
  },

  /**
   * Камера (docs/06 §6). Открытие **не** идемпотентное: повтор должен давать
   * новую сессию, иначе после реконнекта клиент получил бы sessionId уже
   * закрытой камеры.
   */
  viewer: {
    open: (botId: string, viewDistance: number): Promise<ViewerSessionDto> =>
      request<ViewerSessionDto>(`/bots/${botId}/viewer`, {
        method: 'POST',
        body: { viewDistance }
      }),
    close: (sessionId: string): Promise<void> =>
      request<void>(`/viewer/${sessionId}`, { method: 'DELETE' }),
    heartbeat: (sessionId: string): Promise<void> =>
      request<void>(`/viewer/${sessionId}/heartbeat`, { method: 'POST' }),
    sessions: (): Promise<ViewerSessionListDto> => request<ViewerSessionListDto>('/viewer/sessions')
  },

  org: {
    current: (): Promise<OrgCurrentDto> => request<OrgCurrentDto>('/orgs/current')
  },

  /** Аудит (docs/06 §7). Курсорная пагинация: `nextCursor` → следующая страница. */
  audit: {
    list: (query: AuditQuery = {}): Promise<Paginated<AuditEntryDto>> =>
      request<Paginated<AuditEntryDto>>('/audit', { params: { ...query } })
  },

  /**
   * Алерты (docs/06 §7.1). Права — OWNER/ADMIN на все методы, включая чтение:
   * правило хранит адрес доставки.
   */
  alerts: {
    list: (query: AlertRuleListQuery = {}): Promise<Paginated<AlertRuleDto>> =>
      request<Paginated<AlertRuleDto>>('/alerts', { params: { ...query } }),
    get: (id: string): Promise<AlertRuleDto> => request<AlertRuleDto>(`/alerts/${id}`),
    create: (dto: CreateAlertRuleRequest): Promise<AlertRuleDto> =>
      request<AlertRuleDto>('/alerts', { method: 'POST', body: dto, idempotent: true }),
    /**
     * `target` в теле отсутствует, если пользователь его не менял: сервер
     * трактует `undefined` как «не трогать». Отправить туда `targetMasked`
     * нельзя — это не адрес, а маска.
     */
    update: (id: string, dto: UpdateAlertRuleRequest): Promise<AlertRuleDto> =>
      request<AlertRuleDto>(`/alerts/${id}`, { method: 'PATCH', body: dto }),
    remove: (id: string): Promise<void> => request<void>(`/alerts/${id}`, { method: 'DELETE' }),
    /** 200 даже при неудачной доставке: `{ delivered:false, error }` — это результат. */
    test: (id: string): Promise<AlertTestResultDto> =>
      request<AlertTestResultDto>(`/alerts/${id}/test`, { method: 'POST' })
  },

  system: {
    info: (): Promise<SystemInfoDto> => request<SystemInfoDto>('/system/info')
  }
} as const

export type Api = typeof api
